/**
 * Normalize an untrusted `?next=...` value into a safe in-app path.
 *
 * `startsWith("/")` alone is not sufficient — the following all start with `/`
 * but resolve to a different origin:
 *   - `//evil.com/path`          protocol-relative URL
 *   - `/\evil.com`               some browsers treat `\` as `/`
 *   - `/%2Fevil.com`             URL-encoded second slash
 *   - `/%5Cevil.com`             URL-encoded backslash
 *
 * Any of these as a `<a href>` or `window.location.href` would navigate
 * the user off-origin. Return a safe fallback path when the input fails
 * validation rather than trying to "fix" it.
 */
export function safeNextPath(next: string | undefined, fallback = "/"): string {
	if (typeof next !== "string" || next === "") return fallback;
	if (!next.startsWith("/")) return fallback;

	// Second character cannot be another slash, a backslash, or a URL-encoded
	// version of either. This rejects protocol-relative and mixed-slash attacks
	// before we rely on the browser's URL parser.
	const second = next.charAt(1);
	if (second === "/" || second === "\\") return fallback;
	if (next.startsWith("/%2F") || next.startsWith("/%2f")) return fallback;
	if (next.startsWith("/%5C") || next.startsWith("/%5c")) return fallback;

	return next;
}
